import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  CloseOutlined,
  ExportOutlined,
  FileTextOutlined,
  FolderOutlined,
  HistoryOutlined,
  LoadingOutlined,
  PauseOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  SisternodeOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useRequest } from 'ahooks';
import { Avatar, Badge, Col, Divider, Dropdown, Input, Row } from 'antd';
import useNotification from 'antd/es/notification/useNotification';
import IconRound from 'assets/logos/icon-round.svg?react';
import BackButton from 'components/buttons/BackButton';
import PatientAccessTierTag from 'components/chats/PatientAccessTierTag';
import CaregiverProfileModal from 'components/modals/CaregiverProfileModal';
import { Button } from 'components/ui';
import { colors, shadows } from 'design';
import { setConversationStatusValue } from 'hooks/conversationListMutators';
import { useUpdateConversations } from 'hooks/useConversations';
import { patientHistoryQueryOptions, usePatientHistoryCount } from 'hooks/useLoadPatientHistory';
import useSelectConversations from 'hooks/useSelectConversations';
import posthog from 'posthog-js';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { routes } from 'services/RouteService';
import ApiCaregiversManager from 'services/api/ApiCaregiversManager';
import ApiDoctorConversationsManager, { StatusChangeRequestType } from 'services/api/ApiDoctorConversationsManager';
import { selectCurrentUser } from 'store/users/selectors';

/** In-conversation message search UI (passed from the page that owns list state). */
export type ConversationTopBarMessageSearch = {
  open: boolean;
  query: string;
  matchIndex: number;
  matchCount: number;
  showPatientTrigger: boolean;
  showInCaregiverMenu: boolean;
  onOpen: () => void;
  onClose: () => void;
  onQueryChange: (value: string) => void;
  onPrevMatch: () => void;
  onNextMatch: () => void;
};

const defaultMessageSearch: ConversationTopBarMessageSearch = {
  open: false,
  query: '',
  matchIndex: 0,
  matchCount: 0,
  showPatientTrigger: false,
  showInCaregiverMenu: false,
  onOpen: () => {},
  onClose: () => {},
  onQueryChange: () => {},
  onPrevMatch: () => {},
  onNextMatch: () => {},
};

export type RecipientHeaderProps = {
  name?: string;
  avatar?: string | React.ReactNode;
  caregiverId?: string;
  patientId?: string;
  // add some props to control what to display
  showBackButton?: boolean;
  onBackButtonClick?: () => void;
  showSummaryButton?: boolean;
  showHistoryButton?: boolean;
  showActionsButton?: boolean;
  messageSearch?: ConversationTopBarMessageSearch;
};

const useStyles = createUseStyles({
  container: {
    paddingTop: 15,
    paddingLeft: 13,
    paddingRight: 13,
    boxShadow: shadows.sm,
    zIndex: 100,
    backgroundColor: 'white',
    width: '100%',
  },
  actionsContainer: {
    width: '100%',
    padding: '15px 13px',
    backgroundColor: colors.brand.accent,
    alignItems: 'center',
    '& button': {
      color: 'white',
      boxShadow: 'none',
      '& .ant-badge-count': {
        color: colors.brand.accentStrong,
      },
    },
    '& .ant-divider': {
      height: '100%',
      borderColor: colors.brand.accentSubtle,
    },
  },
  actionsRowMain: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'flex-start',
  },
  patientSearchTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    whiteSpace: 'nowrap',
  },
  messageSearchInputWrap: {
    flex: 1,
    minWidth: 0,
    '& .ant-input-affix-wrapper': {
      backgroundColor: colors.brand.accent,
      color: 'white',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
      borderBottom: `1px solid ${colors.brand.accentSubtle}`,
      paddingLeft: 4,
      paddingRight: 4,
    },
    '& input.ant-input': {
      backgroundColor: colors.brand.accent,
      color: 'white',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
      borderBottom: `1px solid ${colors.brand.accentSubtle}`,
    },
    '& .ant-input-affix-wrapper input.ant-input': {
      borderBottom: 'none',
    },
    '& input.ant-input::placeholder': {
      color: 'rgba(255, 255, 255, 0.75)',
    },
  },
  messageSearchCloseCol: {
    flexShrink: 0,
    marginLeft: 8,
    display: 'flex',
    alignItems: 'center',
  },
  messageSearchBarBottomStrip: {
    position: 'relative',
    width: '100%',
    height: 0,
    zIndex: 101,
    pointerEvents: 'none',
  },
  searchFloatingCluster: {
    position: 'absolute',
    right: 13,
    top: 10,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    pointerEvents: 'auto',
  },
  searchMatchPill: {
    backgroundColor: 'rgba(255, 255, 255, 0.96)',
    color: colors.brand.accentStrong,
    fontSize: 13,
    fontWeight: 600,
    padding: '7px 12px',
    borderRadius: 20,
    boxShadow: shadows.md,
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
  },
  searchRoundButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  searchRoundBtn: {
    width: '44px',
    height: '44px',
    minWidth: '44px',
    maxWidth: '44px',
    minHeight: '44px',
    maxHeight: '44px',
    padding: '0',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    lineHeight: 1,
    boxShadow: shadows.md,
    border: `1px solid ${colors.brand.accentSubtle}`,
    color: colors.brand.accentStrong,
    '& .ant-btn-icon': {
      marginInlineEnd: '0',
      marginInlineStart: '0',
    },
  },
  actionLabel: {
    color: colors.text.tertiary,
  },
  actionDetails: {
    color: colors.text.disabled,
    fontSize: 12,
    lineHeight: 1.2,
    marginTop: 4,
    maxWidth: '60vw',
  },
  disabledOption: {
    color: colors.text.tertiary,
    opacity: 0.6,
  },
  columnBackButton: {
    marginRight: 10,
    marginBottom: 12,
    flexShrink: 0,
  },
  columnAvatar: {
    marginBottom: 12,
    marginRight: 5,
    flexShrink: 0,
  },
  columnName: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    marginBottom: 12,
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  name: {
    margin: 0,
    fontSize: 18,
    marginBottom: '0',
  },
  nameStatus: {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 4,
    padding: '3px 8px',
    borderRadius: 6,
    backgroundColor: colors.surface.page,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.2,
    color: colors.text.tertiary,
    whiteSpace: 'nowrap',
    '& .anticon': {
      fontSize: 12,
    },
  },
  caregiverList: {},
  caregiverItem: {
    backgroundColor: colors.surface.page,
    borderRadius: 5,
    marginTop: 5,
    cursor: 'pointer',
  },
  menuIndentedItem: {
    display: 'block',
    paddingLeft: 22,
  },
  menuTopAlignedIconItem: {
    '& .ant-dropdown-menu-item-icon': {
      alignSelf: 'flex-start',
      marginTop: 5,
    },
  },
  actionsMenu: {
    // Unbounded, this can grow past the viewport on a conversation with a
    // long caregiver-transfer history — the portal then pushes past the
    // bottom of the screen and the whole document scrolls to reach it,
    // instead of just the menu itself.
    maxHeight: '70vh',
    overflowY: 'auto',
    '& .ant-dropdown-menu-item-icon': {
      color: colors.text.tertiary,
    },
  },
});

const ConversationTopBar: React.FC<RecipientHeaderProps> = ({
  name = undefined,
  avatar = undefined,
  caregiverId = undefined,
  patientId = undefined,
  showBackButton = false,
  onBackButtonClick = undefined,
  showSummaryButton = false,
  showHistoryButton = false,
  showActionsButton = false,
  messageSearch: messageSearchProp,
}) => {
  const messageSearch = messageSearchProp ?? defaultMessageSearch;
  // Hooks
  const styles = useStyles();
  const { t } = useTranslation();
  const [api, context] = useNotification();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updateConversations = useUpdateConversations();

  // Selectors
  const currentUser = useSelector(selectCurrentUser);
  const { conversationId, conversationMetadata } = useSelectConversations();

  const urlParams = useParams();
  // TODO this is likely bad coupling between the router and this component's logic
  const viewedConversationId = urlParams.historicConversation || conversationId;
  const { pathname } = useLocation();
  const isOnDossier = pathname.endsWith('/dossier');

  // State
  const [openProfileModal, setOpenProfileModal] = useState(false);

  function onBackButtonClicked() {
    if (onBackButtonClick) {
      onBackButtonClick();
    }
  }

  const {
    loading: loadingGetCaregiverGroupsList,
    run: getCaregiverGroupsList,
    data,
  } = useRequest(
    () => {
      const patientId = conversationMetadata?.conversation.patient.id;
      if (patientId) {
        return ApiCaregiversManager.getAvailableCaregiverGroupsForPatient({ patientId });
      }
      return ApiCaregiversManager.getAvailableCaregiverGroupsList();
    },
    {
      manual: true,
      onError: () => {
        api.error({
          message: t('caregivers.list.errors.fetch'),
        });
      },
    },
  );

  const { loading: loadingRequestOwnerChange } = useRequest(
    caregiverGroupId => ApiDoctorConversationsManager.requestOwnerChange({ caregiverGroupId, conversationId }),
    {
      manual: true,
      onSuccess: () => {
        api.success({
          message: t('caregivers.actions.requestOwnerChange.success'),
        });
        onBackButtonClicked();
      },
      onError: () => {
        api.error({
          message: t('caregivers.actions.requestOwnerChange.error'),
        });
      },
    },
  );

  const { loading: loadingCreateNewConversationWithGroup, run: createNewConversationWithGroup } = useRequest(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (caregiverGroupId: string, _patientId: string) =>
      ApiDoctorConversationsManager.createNewConversationWithGroup({ caregiverGroupId, conversationId }),
    {
      manual: true,
      onSuccess: (_data, [, patientId]) => {
        api.success({
          message: t('caregivers.actions.createNewConversationWithGroup.success'),
        });
        queryClient.invalidateQueries({ queryKey: patientHistoryQueryOptions(patientId).queryKey });
      },
      onError: () => {
        api.error({
          message: t('caregivers.actions.createNewConversationWithGroup.error'),
        });
      },
    },
  );

  const { loading: loadingRequestCaregiverAssignment, run: requestCaregiverAssignment } = useRequest(
    registeredCaregiverId =>
      ApiDoctorConversationsManager.requestCaregiverAssignment({ registeredCaregiverId, conversationId }),
    {
      manual: true,
      onSuccess: () => {
        api.success({
          message: t('caregivers.actions.requestCaregiverAssignment.success'),
        });
        onBackButtonClicked();
      },
      onError: () => {
        api.error({
          message: t('caregivers.actions.requestCaregiverAssignment.error'),
        });
      },
    },
  );

  const { loading: loadingStatusChange, run: requestStatusChange } = useRequest(
    (newStatus: StatusChangeRequestType) =>
      ApiDoctorConversationsManager.requestConversationStatusChange(conversationId, newStatus),
    {
      manual: true,
      onSuccess: (_data, params) => {
        api.success({
          message: t('caregivers.actions.statusChange.success'),
        });
        // Optimistically update the conversation row's status; the segmented bucket
        // (closed / onHold) is then derived from `status.value` by getConversationType,
        // and the websocket `conversation.status.updated` event will confirm it later.
        updateConversations(setConversationStatusValue(conversationId, params[0]));
        onBackButtonClicked();
      },
      onError: () => {
        api.error({
          message: t('caregivers.actions.statusChange.error'),
        });
      },
    },
  );

  function transfer() {
    getCaregiverGroupsList();
    getCaregiversFromHistory();
  }

  function selectCaregiverGroup(caregiverGroupId: string) {
    createNewConversationWithGroup(caregiverGroupId, conversationMetadata?.conversation.patient.id ?? '');
  }

  function selectCaregiver(caregiverId: string) {
    requestCaregiverAssignment(caregiverId);
  }

  const { run: getCaregiversFromHistory, data: caregiversFromHistory } = useRequest(
    () => ApiDoctorConversationsManager.getInvolvedRegisteredCaregivers(conversationId),
    {
      manual: true,
      onError: () => {
        api.error({
          message: t('caregivers.list.errors.fetch'),
        });
      },
    },
  );

  const normalizedCaregiversFromHistory = React.useMemo(() => {
    const list = caregiversFromHistory || [];
    return list
      .map((rc: any) => ({
        id: rc?.user?.id ?? rc?.id,
        name: rc?.caregiver?.display_name ?? rc?.display_name,
        photo_url: rc?.user?.photo_url ?? rc?.photo_url,
        groups: rc?.caregiver?.caregiver_groups ?? rc?.caregiver_groups ?? [],
      }))
      .filter(c => c.id && c.id !== currentUser?.id);
  }, [caregiversFromHistory, currentUser?.id]);

  // (removed duplicate)

  function shareConversationToTeam() {
    const apiHost = import.meta.env.VITE_API_SERVER_URL;
    const dashboardUrl = `${apiHost}/dashboard/conversations/${viewedConversationId}/`;

    navigator.clipboard
      .writeText(dashboardUrl)
      .then(() => {
        api.success({
          message: t('chats.actions.shareToTeamSuccess'),
        });
      })
      .catch(() => {
        api.error({
          message: t('global.errors.unknown'),
        });
      });
  }

  const menuProps = {
    className: styles.actionsMenu,
    items: [
      ...(messageSearch.showInCaregiverMenu
        ? [
            {
              key: 'search_messages',
              icon: <SearchOutlined />,
              label: <span>{t('chats.actions.searchMessages')}</span>,
            },
            {
              type: 'divider' as const,
              key: 'divider_search_messages',
            },
          ]
        : []),
      {
        key: 'share_to_team',
        icon: <ExportOutlined />,
        label: <span>{t('chats.actions.shareToTeam')}</span>,
      },
      {
        type: 'divider' as const,
        key: 'divider_share',
      },
      {
        key: 'put_on_hold',
        icon: <PauseOutlined />,
        className: styles.menuTopAlignedIconItem,
        label: (
          <>
            <span>{t('chats.actions.putOnHold')}</span>
            <div className={styles.actionDetails}>{t('chats.actions.putOnHoldDetails')}</div>
          </>
        ),
      },
      {
        type: 'divider' as const,
        key: 'divider_1',
      },
      {
        key: 'close',
        icon: <CheckOutlined />,
        className: styles.menuTopAlignedIconItem,
        label: (
          <>
            <span>{t('chats.actions.close')}</span>
            <div className={styles.actionDetails}>{t('chats.actions.putOnHoldDetails')}</div>
          </>
        ),
      },
      {
        type: 'divider' as const,
        key: 'divider_2',
      },
      ...(normalizedCaregiversFromHistory.length > 0
        ? [
            {
              key: 'assign_caregiver',
              icon: <SwapOutlined />,
              label: <span className={styles.actionLabel}>{t('chats.actions.reassignToCaregiver')}</span>,
            },
            ...normalizedCaregiversFromHistory.map(caregiver => ({
              key: `assign_caregiver.${caregiver.id}`,
              label: (
                <span
                  className={styles.menuIndentedItem}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <Avatar size="small" src={caregiver.photo_url} />
                  <span>
                    {caregiver.name}
                    {Array.isArray(caregiver.groups) && caregiver.groups.length > 0 && (
                      <div className={styles.actionDetails}>
                        {caregiver.groups.map((g: any) => g.display_name || g.name).join(', ')}
                      </div>
                    )}
                  </span>
                </span>
              ),
            })),
          ]
        : []),
      {
        key: 'assign',
        icon: <SisternodeOutlined />,
        label: <span className={styles.actionLabel}>{t('chats.actions.transferTo')}</span>,
      },
      ...(data || []).map(d => ({
        // CaregiverGroupDTO typing doesn't include this field, but the API may return it.
        // Keep runtime behavior while staying type-safe.
        key: `assign.${d.id}`,
        label: (
          <span className={styles.menuIndentedItem}>
            <span className={(d as any).has_ongoing_conversation ? styles.disabledOption : undefined}>
              {d.display_name}
              {(d as any).has_ongoing_conversation && ` - ${t('chats.actions.ongoing', 'En cours')}`}
            </span>
          </span>
        ),
        disabled: Boolean((d as any).has_ongoing_conversation),
      })),
    ],
    onClick: (e: any) => {
      // Don't process clicks on disabled items
      const menuItem = menuProps.items.find(item => item.key === e.key);
      if (menuItem && 'disabled' in menuItem && menuItem.disabled) {
        return;
      }

      if (e.key === 'search_messages') messageSearch.onOpen();
      else if (e.key === 'share_to_team') shareConversationToTeam();
      else if (e.key === 'close') requestStatusChange('CLOSED');
      else if (e.key === 'put_on_hold') requestStatusChange('ON_HOLD');
      else if (e.key.startsWith('assign.')) selectCaregiverGroup(e.key.replace('assign.', ''));
      else if (e.key.startsWith('assign_caregiver.')) selectCaregiver(e.key.replace('assign_caregiver.', ''));
    },
  };
  const styleIsClickable = {
    cursor: 'pointer',
  };

  const { count: historyCount, loading: historyCountLoading } = usePatientHistoryCount(
    conversationMetadata?.conversation.patient.id ?? '',
  );

  function buildPrimaryActionsBar() {
    const components = [];
    if (!currentUser?.is_caregiver && conversationId && conversationMetadata?.conversation.current_owner) {
      // Patients: "Mes documents" button (only show if there's a caregiver assigned)
      components.push(
        <Button
          variant="ghost"
          key="my-documents-button"
          size="sm"
          icon={<FileTextOutlined />}
          onClick={() => {
            navigate(routes.patientDocuments.route.replace(':conversationId', conversationId));
            posthog.capture('patient_documents.page_viewed');
          }}
        >
          {t('chats.actions.myDocuments')}
        </Button>,
      );
      if (messageSearch.showPatientTrigger && !messageSearch.open) {
        components.push(
          <Button
            variant="ghost"
            key="search-messages-button"
            size="sm"
            icon={<SearchOutlined />}
            aria-label={t('chats.actions.searchMessages')}
            onClick={() => messageSearch.onOpen()}
            className={styles.patientSearchTrigger}
          >
            {t('chats.actions.searchShortLabel')}
          </Button>,
        );
      }
    }
    if (currentUser?.is_caregiver && showSummaryButton) {
      components.push(
        <Button
          variant="ghost"
          key="dossier-button"
          size="sm"
          icon={<FolderOutlined />}
          onClick={() => {
            navigate(routes.caregiverDossier.route.replace(':conversationId', viewedConversationId));
            posthog.capture('patient_file.page_viewed');
          }}
        >
          {t('chats.actions.dossier')}
        </Button>,
      );
    }
    if (currentUser?.is_caregiver) {
      if (showHistoryButton) {
        components.push(
          <Button
            variant="ghost"
            key="show-history-button"
            size="sm"
            icon={<HistoryOutlined />}
            onClick={() =>
              navigate(routes.patientConversationsHistory.route.replace(':conversationId', conversationId))
            }
          >
            {t('chats.actions.history')}
            {historyCountLoading ? (
              <LoadingOutlined style={{ marginLeft: 4 }} />
            ) : (
              historyCount > 1 && <Badge color="white" count={historyCount} />
            )}
          </Button>,
        );
      }
      if (showActionsButton) {
        components.push(
          <Dropdown
            key="actions-dropdown"
            menu={menuProps}
            placement="bottomRight"
            arrow
            onOpenChange={open => {
              if (open) {
                transfer();
              }
            }}
          >
            <Button
              variant="ghost"
              key="show-action-button"
              size="sm"
              icon={<PlusCircleOutlined />}
              disabled={
                loadingGetCaregiverGroupsList ||
                loadingRequestOwnerChange ||
                loadingStatusChange ||
                loadingRequestCaregiverAssignment ||
                loadingCreateNewConversationWithGroup
              }
              loading={
                loadingGetCaregiverGroupsList ||
                loadingRequestOwnerChange ||
                loadingStatusChange ||
                loadingRequestCaregiverAssignment ||
                loadingCreateNewConversationWithGroup
              }
            >
              {t('chats.actions.button')}
            </Button>
          </Dropdown>,
        );
      }
    }
    return components;
  }

  const primaryActionsBar = buildPrimaryActionsBar()
    .flatMap((button, index) => [button, <Divider key={`divider-${index}`} type="vertical" />])
    .slice(0, -1);

  const showActionsRow = messageSearch.open || primaryActionsBar.length > 0;

  return (
    <>
      <Row className={styles.container} align="middle">
        {context}
        {openProfileModal && caregiverId && (
          <CaregiverProfileModal caregiverId={caregiverId} open onClose={() => setOpenProfileModal(false)} />
        )}
        {showBackButton && (
          <Col className={styles.columnBackButton}>
            <BackButton onClick={() => onBackButtonClicked()} />
          </Col>
        )}
        {avatar && (
          <Col
            className={styles.columnAvatar}
            onClick={() => {
              if (patientId && !isOnDossier) {
                navigate(routes.caregiverDossier.route.replace(':conversationId', viewedConversationId));
              } else if (caregiverId) {
                setOpenProfileModal(true);
              }
            }}
            style={(patientId && !isOnDossier) || caregiverId ? styleIsClickable : undefined}
          >
            {typeof avatar === 'string' && avatar.startsWith('http') ? (
              <Avatar src={avatar} />
            ) : (
              <Avatar icon={<IconRound fill={colors.brand.accent} />} style={{ backgroundColor: 'transparent' }} />
            )}
          </Col>
        )}
        <Col
          className={styles.columnName}
          onClick={() => {
            if (patientId && !isOnDossier) {
              navigate(routes.caregiverDossier.route.replace(':conversationId', viewedConversationId));
            } else if (caregiverId) {
              setOpenProfileModal(true);
            }
          }}
          style={(patientId && !isOnDossier) || caregiverId ? styleIsClickable : undefined}
        >
          <h3 className={styles.name}>{name}</h3>
          {currentUser?.is_caregiver && conversationMetadata?.conversation.patient.access_tier && (
            <PatientAccessTierTag
              accessTier={conversationMetadata.conversation.patient.access_tier}
              className={styles.nameStatus}
            />
          )}
        </Col>
      </Row>
      {showActionsRow && (
        <>
          <Row className={styles.actionsContainer} wrap={false}>
            {messageSearch.open ? (
              <>
                <Col flex="auto" style={{ minWidth: 0 }}>
                  <div className={styles.messageSearchInputWrap}>
                    <Input
                      autoFocus
                      placeholder={t('chats.actions.searchMessagesPlaceholder')}
                      value={messageSearch.query}
                      onChange={e => messageSearch.onQueryChange(e.target.value)}
                      aria-label={t('chats.actions.searchMessages')}
                    />
                  </div>
                </Col>
                <Col className={styles.messageSearchCloseCol}>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<CloseOutlined />}
                    aria-label={t('chats.actions.closeMessageSearch')}
                    onClick={() => messageSearch.onClose()}
                  />
                </Col>
              </>
            ) : (
              <Col flex="auto" className={styles.actionsRowMain}>
                {primaryActionsBar}
              </Col>
            )}
          </Row>
          {messageSearch.open && messageSearch.matchCount > 0 ? (
            <div className={styles.messageSearchBarBottomStrip}>
              <div className={styles.searchFloatingCluster}>
                <span className={styles.searchMatchPill} data-testid="message-search-match-pill">
                  {t('chats.actions.searchMatchCounter', {
                    current: messageSearch.matchIndex + 1,
                    total: messageSearch.matchCount,
                  })}
                </span>
                <div className={styles.searchRoundButtons}>
                  <Button
                    shape="circle"
                    icon={<ArrowUpOutlined />}
                    aria-label={t('chats.actions.searchPreviousMatch')}
                    testId="message-search-prev-bar"
                    className={styles.searchRoundBtn}
                    onClick={() => messageSearch.onPrevMatch()}
                  />
                  <Button
                    shape="circle"
                    icon={<ArrowDownOutlined />}
                    aria-label={t('chats.actions.searchNextMatch')}
                    testId="message-search-next-bar"
                    className={styles.searchRoundBtn}
                    onClick={() => messageSearch.onNextMatch()}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  );
};
export default ConversationTopBar;
